import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: 'admin' | 'editor' | 'author';
}

interface AuthorProfile {
  id: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  social_links: Record<string, string>;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  profile: AuthorProfile | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getAdminToken: () => Promise<string | null>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [profile, setProfile] = useState<AuthorProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const { data: session } = await supabase
        .from('admin_sessions')
        .select('*, admin_user:admin_users(*)')
        .eq('token', token)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (session && session.admin_user) {
        setUser(session.admin_user as AdminUser);
        await loadProfile(session.admin_user.id);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('author_profiles')
        .select('*')
        .eq('admin_user_id', userId)
        .maybeSingle();

      if (data) {
        setProfile(data as AuthorProfile);
      }
    } catch (error) {
      console.error('Profile load error:', error);
    }
  };

  const login = async (username: string, password: string) => {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const { token, user: userData } = await response.json();

    localStorage.setItem('admin_token', token);
    setUser(userData);
    await loadProfile(userData.id);
  };

  const logout = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      await supabase.from('admin_sessions').delete().eq('token', token);
    }
    localStorage.removeItem('admin_token');
    setUser(null);
    setProfile(null);
  };

  const getAdminToken = async () => {
    return localStorage.getItem('admin_token');
  };

  return (
    <AdminAuthContext.Provider value={{ user, profile, loading, login, logout, getAdminToken }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
